import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Crown, Sparkles, ShieldCheck, ChevronRight, AlertTriangle, Zap } from "lucide-react";

// Inline plan banner shown at the top of the /sell page.
// Keeps vendors aware of their trial countdown, plan status, and upgrade CTAs.
export default function PlanStatusBanner() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    api.get("/subscriptions/me")
      .then(({ data: d }) => { if (mounted) setData(d); })
      .catch(() => { /* ignore */ });
    return () => { mounted = false; };
  }, []);

  if (!data?.is_vendor || !data.entitlements) return null;
  const ent = data.entitlements;

  const palette = {
    trial: {
      Icon: Sparkles, label: "Free Trial",
      tone: "from-cyan-400/15 to-pink-500/15 border-cyan-400/30",
      iconTone: "from-cyan-400 to-pink-500",
    },
    basic: {
      Icon: ShieldCheck, label: "Basic Vendor",
      tone: "from-emerald-400/15 to-transparent border-emerald-400/30",
      iconTone: "from-emerald-400 to-cyan-400",
    },
    featured: {
      Icon: Crown, label: "Featured Vendor",
      tone: "from-amber-300/15 to-pink-500/15 border-amber-400/40",
      iconTone: "from-amber-300 to-pink-500",
    },
    vip: {
      Icon: Crown, label: "VIP Member",
      tone: "from-amber-300/20 via-pink-500/15 to-cyan-400/15 border-amber-400/50",
      iconTone: "from-amber-300 via-pink-500 to-cyan-400",
    },
    inactive: {
      Icon: AlertTriangle, label: "Shop inactive",
      tone: "from-red-500/15 to-transparent border-red-400/30",
      iconTone: "from-red-500 to-amber-400",
    },
  };
  const P = palette[ent.plan] || palette.trial;
  const Icon = P.Icon;

  return (
    <button
      onClick={() => navigate("/sell/upgrade")}
      className={`w-full text-left rounded-2xl p-4 bg-gradient-to-br ${P.tone} border flex items-center gap-3 hover:brightness-110 transition`}
      data-testid="plan-status-banner"
    >
      <div className={`w-10 h-10 shrink-0 rounded-xl bg-gradient-to-br ${P.iconTone} grid place-items-center`}>
        <Icon className="w-5 h-5 text-black" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-[10px] uppercase tracking-widest text-zinc-300 font-bold">{P.label}</p>
          {ent.founding_vendor && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-amber-300 to-pink-500 text-black text-[9px] font-black uppercase tracking-widest" data-testid="founding-pill">
              <Crown className="w-2.5 h-2.5" /> Founding
            </span>
          )}
        </div>
        <p className="font-bold text-white text-sm mt-0.5 truncate" data-testid="banner-message">
          {ent.is_vip
            ? <>You're a VIP · all features unlocked · no fees · tap to view perks</>
            : ent.is_trial
            ? <>{ent.trial_days_left} days left of full access · tap to see plans</>
            : ent.plan === "inactive"
            ? <>Your shop is paused — pick a plan to come back online</>
            : ent.cancel_at_period_end
            ? <>Auto-renew off · active until {new Date(ent.current_period_end).toLocaleDateString(undefined,{month:"short",day:"numeric"})}</>
            : <>Renews {new Date(ent.current_period_end).toLocaleDateString(undefined,{month:"short",day:"numeric"})} · tap to upgrade or boost</>}
        </p>
      </div>
      <div className="flex items-center gap-1 shrink-0 text-pink-300 text-sm font-bold">
        <Zap className="w-3.5 h-3.5" /> Boost
        <ChevronRight className="w-4 h-4" />
      </div>
    </button>
  );
}
